/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { KeycloakTokenService } from '@administration/keycloak-shared';
import { ApiRootResource, ApiRootService } from '@common/api-root-shared';
import { NavigationService } from '@alfa-client/navigation-shared';
import { StateResource } from '@common/tech-shared';
import { buildPathSegmentsFromLocalStorage } from '@alfa-client/vorgang-shared';
import { Component, inject, OnInit } from '@angular/core';
import { Params } from '@angular/router';
import { AuthenticationConfig, AuthenticationService } from '@common/authentication';
import { Observable } from 'rxjs';

@Component({
  selector: 'alfa-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  public apiRootStateResource$: Observable<StateResource<ApiRootResource>>;

  private readonly authenticationService = inject(AuthenticationService);
  private readonly apiRootService = inject(ApiRootService);
  private readonly keycloakTokenService = inject(KeycloakTokenService);
  private readonly navigationService = inject(NavigationService);

  private readonly queryParamsToRemove: string[] = ['iss', 'state', 'session_state', 'code'];

  ngOnInit(): void {
    this.authenticationService.login(this.buildAuthenticationConfig()).then(() => this._doAfterLoggedIn());
  }

  private buildAuthenticationConfig(): AuthenticationConfig {
    return { redirectUriSuffix: this._buildRedirectUriSuffix(), waitForInitialNavgiationToComplete: false };
  }

  _buildRedirectUriSuffix(): string {
    const path: string = this._buildInitialPath() + window.location.search;
    return removeQueryParams(path, this.queryParamsToRemove);
  }

  _doAfterLoggedIn(): void {
    this.apiRootStateResource$ = this.apiRootService.getApiRoot();
    this.keycloakTokenService.registerAccessTokenProvider();
    this.navigationService.navigate(this._buildInitialPath(), this._buildInitialQueryParams());
  }

  _buildInitialPath(): string {
    const currentPath: string = window.location.pathname;
    if (currentPath === '/') {
      return currentPath + buildPathSegmentsFromLocalStorage().join('/');
    }
    return currentPath;
  }

  _buildInitialQueryParams(): Params | undefined {
    return buildParams(window.location.search, this.queryParamsToRemove);
  }
}

function removeQueryParams(url: string, paramsToRemove: string[]): string {
  if (!url.includes('?')) {
    return url;
  }
  const [baseUrl, queryString]: string[] = url.split('?');

  const normalizedQueryString: string = queryString.replace(/\?/g, '&');

  const searchParams: URLSearchParams = new URLSearchParams(normalizedQueryString);
  paramsToRemove.forEach((param: string) => searchParams.delete(param));
  const remainingParams: string = searchParams.toString();
  return remainingParams ? `${baseUrl}?${remainingParams}` : baseUrl;
}

function buildParams(url: string, paramsToIgnore: string[] = []): Params {
  const queryParams: URLSearchParams = new URLSearchParams(url);
  if (!queryParams.toString()) return undefined;

  const params: Params = {};
  queryParams.forEach((value: string, key: string) => {
    if (!paramsToIgnore.includes(key)) params[key] = value;
  });
  return params;
}
